import { useEffect, useMemo, useState } from "react";
import DateInput from "@/components/DateInput";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Plus, PencilSimple, Trash, Tag, CheckCircle, XCircle, MagnifyingGlass, Infinity as InfinityIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import NumberInput from "@/components/ui/NumberInput";

const TIPO_DESCUENTO = [
  { value: "porcentaje", label: "% (porcentaje)" },
  { value: "monto_por_unidad", label: "$ por unidad" },
  { value: "precio_fijo", label: "Precio fijo por unidad" },
];

const DIAS = [
  { v: 0, l: "Lun" }, { v: 1, l: "Mar" }, { v: 2, l: "Mié" },
  { v: 3, l: "Jue" }, { v: 4, l: "Vie" }, { v: 5, l: "Sáb" }, { v: 6, l: "Dom" },
];
const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6];

const emptyForm = () => ({
  nombre: "",
  descripcion: "",
  codigo: "",
  descuento: { tipo: "porcentaje", valor: 10 },
  condiciones: {
    fecha_desde: "",
    fecha_hasta: "",
    fecha_hasta_indefinida: true,
    dias_semana: [],
    cliente_ids: [],
    lista_precios_ids: [],
    producto_ids: [],
    cantidad_min_kg: "",
    cantidad_max_kg: "",
    monto_min_factura: "",
    cantidad_max_kg_promo: "",
    cantidad_max_kg_indefinida: true,
    cantidad_max_unidades_promo: "",
    cantidad_max_unidades_indefinida: true,
    medios_pago: [],
  },
  acumulable: false,
  activo: true,
});

export default function PromocionesPage() {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [productos, setProductos] = useState([]);
  const [listasPrecios, setListasPrecios] = useState([]);
  const [cajas, setCajas] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: ps }, { data: cs }, { data: prods }, { data: lps }, { data: cjs }] = await Promise.all([
        api.get("/promociones"),
        api.get("/clientes").catch(() => ({ data: [] })),
        api.get("/productos").catch(() => ({ data: [] })),
        api.get("/listas-precios").catch(() => ({ data: [] })),
        api.get("/cajas").catch(() => ({ data: [] })),
      ]);
      setPromos(ps || []);
      setClientes(cs || []);
      setProductos(prods || []);
      setListasPrecios(lps || []);
      setCajas(cjs || []);
    } catch (e) {
      toast.error("Error cargando promociones");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const abrirNuevo = () => { setEditing(null); setForm(emptyForm()); setDialogOpen(true); };

  const abrirEdit = (p) => {
    setEditing(p);
    const c = p.condiciones || {};
    setForm({
      nombre: p.nombre || "",
      descripcion: p.descripcion || "",
      codigo: p.codigo || "",
      descuento: p.descuento || { tipo: "porcentaje", valor: 10 },
      condiciones: {
        fecha_desde: c.fecha_desde || "",
        fecha_hasta: c.fecha_hasta || "",
        fecha_hasta_indefinida: !c.fecha_hasta,
        dias_semana: c.dias_semana || [],
        cliente_ids: c.cliente_ids || [],
        lista_precios_ids: c.lista_precios_ids || [],
        producto_ids: c.producto_ids || [],
        cantidad_min_kg: c.cantidad_min_kg ?? "",
        cantidad_max_kg: c.cantidad_max_kg ?? "",
        monto_min_factura: c.monto_min_factura ?? "",
        cantidad_max_kg_promo: c.cantidad_max_kg_promo ?? "",
        cantidad_max_kg_indefinida: c.cantidad_max_kg_promo == null,
        cantidad_max_unidades_promo: c.cantidad_max_unidades_promo ?? "",
        cantidad_max_unidades_indefinida: c.cantidad_max_unidades_promo == null,
        medios_pago: c.medios_pago || [],
      },
      acumulable: p.acumulable || false,
      activo: p.activo !== false,
    });
    setDialogOpen(true);
  };

  const toggleDia = (v) => {
    const arr = form.condiciones.dias_semana || [];
    const nuevo = arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v].sort();
    setForm({ ...form, condiciones: { ...form.condiciones, dias_semana: nuevo } });
  };
  const setSemanaCompleta = () => {
    const all = form.condiciones.dias_semana?.length === 7;
    setForm({
      ...form,
      condiciones: { ...form.condiciones, dias_semana: all ? [] : [...ALL_DAYS] },
    });
  };

  const setC = (key, val) => setForm({ ...form, condiciones: { ...form.condiciones, [key]: val } });

  const submit = async () => {
    if (!form.nombre.trim()) { toast.error("El nombre es obligatorio"); return; }
    if (form.descuento.valor === "" || form.descuento.valor === null || Number(form.descuento.valor) < 0) {
      toast.error("Ingresá un valor de descuento válido"); return;
    }
    const cond = form.condiciones;
    const payload = {
      nombre: form.nombre.trim(),
      descripcion: form.descripcion?.trim() || null,
      codigo: form.codigo?.trim() || null,
      descuento: { tipo: form.descuento.tipo, valor: Number(form.descuento.valor) },
      condiciones: {
        fecha_desde: cond.fecha_desde || null,
        fecha_hasta: cond.fecha_hasta_indefinida ? null : (cond.fecha_hasta || null),
        dias_semana: (cond.dias_semana || []).length ? cond.dias_semana : null,
        cliente_ids: (cond.cliente_ids || []).length ? cond.cliente_ids : null,
        lista_precios_ids: (cond.lista_precios_ids || []).length ? cond.lista_precios_ids : null,
        producto_ids: (cond.producto_ids || []).length ? cond.producto_ids : null,
        cantidad_min_kg: cond.cantidad_min_kg === "" ? null : Number(cond.cantidad_min_kg),
        cantidad_max_kg: cond.cantidad_max_kg === "" ? null : Number(cond.cantidad_max_kg),
        monto_min_factura: cond.monto_min_factura === "" ? null : Number(cond.monto_min_factura),
        cantidad_max_kg_promo: cond.cantidad_max_kg_indefinida
          ? null
          : (cond.cantidad_max_kg_promo === "" ? null : Number(cond.cantidad_max_kg_promo)),
        cantidad_max_unidades_promo: cond.cantidad_max_unidades_indefinida
          ? null
          : (cond.cantidad_max_unidades_promo === "" ? null : Number(cond.cantidad_max_unidades_promo)),
        medios_pago: (cond.medios_pago || []).length ? cond.medios_pago : null,
      },
      acumulable: !!form.acumulable,
      activo: !!form.activo,
    };
    try {
      if (editing) {
        await api.put(`/promociones/${editing.id}`, payload);
        toast.success("Promoción actualizada");
      } else {
        await api.post("/promociones", payload);
        toast.success("Promoción creada");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    }
  };

  const eliminar = async (p) => {
    if (!confirm(`¿Eliminar la promoción "${p.nombre}"?`)) return;
    try {
      await api.delete(`/promociones/${p.id}`);
      toast.success("Promoción eliminada");
      load();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const descripcionDescuento = (p) => {
    const d = p.descuento;
    if (!d) return "-";
    if (d.tipo === "porcentaje") return `-${d.valor}%`;
    if (d.tipo === "monto_por_unidad") return `-$ ${d.valor} / unidad`;
    if (d.tipo === "precio_fijo") return `$ ${d.valor} c/u (fijo)`;
    return "-";
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-heading flex items-center gap-2">
            <Tag size={26} weight="fill" className="text-[#B84A52]" /> Promociones y Descuentos
          </h1>
          <p className="text-sm text-neutral-500">
            Las promos activas se aplican automáticamente al facturar cuando se cumplen sus condiciones.
          </p>
        </div>
        <Button onClick={abrirNuevo} data-testid="promo-nueva-btn">
          <Plus size={16} className="mr-2" /> Nueva promoción
        </Button>
      </div>

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-100 text-left">
              <tr>
                <th className="p-2 w-14 text-center">Estado</th>
                <th className="p-2">Nombre</th>
                <th className="p-2 w-24">Código</th>
                <th className="p-2 w-40">Descuento</th>
                <th className="p-2 w-56">Vigencia · Días</th>
                <th className="p-2 w-40">Condiciones</th>
                <th className="p-2 w-32">Consumo kg</th>
                <th className="p-2 w-24 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {promos.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-neutral-400">
                    {loading ? "Cargando…" : 'Aún no creaste promociones. Hacé clic en "Nueva promoción" para empezar.'}
                  </td>
                </tr>
              ) : promos.map((p) => {
                const c = p.condiciones || {};
                const semanaCompleta = !c.dias_semana || c.dias_semana.length === 0 || c.dias_semana.length === 7;
                const maxKg = c.cantidad_max_kg_promo;
                const consumo = p.kilos_consumidos || 0;
                return (
                  <tr key={p.id} className={`border-t hover:bg-neutral-50 ${!p.activo ? "opacity-60" : ""}`} data-testid={`promo-row-${p.id}`}>
                    <td className="p-2 text-center">
                      {p.activo ? <CheckCircle size={20} className="inline text-emerald-600" /> : <XCircle size={20} className="inline text-neutral-400" />}
                    </td>
                    <td className="p-2">
                      <div className="font-medium">{p.nombre}</div>
                      {p.descripcion && <div className="text-xs text-neutral-500">{p.descripcion}</div>}
                    </td>
                    <td className="p-2 font-mono">{p.codigo || <span className="text-neutral-400">-</span>}</td>
                    <td className="p-2 font-semibold text-[#B84A52]">{descripcionDescuento(p)}</td>
                    <td className="p-2 text-xs">
                      <div>
                        {c.fecha_desde || "—"} → {c.fecha_hasta || <span className="text-emerald-700">Indefinido</span>}
                      </div>
                      <div className="text-neutral-500 mt-0.5">
                        {semanaCompleta
                          ? <span className="text-neutral-500">Toda la semana</span>
                          : c.dias_semana.map((d) => DIAS[d].l).join(" · ")}
                      </div>
                    </td>
                    <td className="p-2 text-xs space-y-0.5">
                      {c.cliente_ids?.length ? <Badge variant="outline" className="mr-1">{c.cliente_ids.length} cli</Badge> : null}
                      {c.producto_ids?.length ? <Badge variant="outline" className="mr-1">{c.producto_ids.length} prod</Badge> : null}
                      {c.lista_precios_ids?.length ? <Badge variant="outline" className="mr-1">Listas</Badge> : null}
                      {c.medios_pago?.length ? <Badge variant="outline" className="mr-1">Pago</Badge> : null}
                      {c.monto_min_factura ? <Badge variant="outline" className="mr-1">Min ${c.monto_min_factura}</Badge> : null}
                      {c.cantidad_min_kg ? <Badge variant="outline">≥{c.cantidad_min_kg} kg</Badge> : null}
                      {c.cantidad_max_kg ? <Badge variant="outline">≤{c.cantidad_max_kg} kg</Badge> : null}
                    </td>
                    <td className="p-2 text-xs font-mono">
                      {maxKg == null && c.cantidad_max_unidades_promo == null && (
                        <span className="text-emerald-700">sin tope</span>
                      )}
                      {maxKg != null && (
                        <>
                          <div>{consumo.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} / {Number(maxKg).toLocaleString("es-AR")} kg</div>
                          <div className="h-1 bg-neutral-200 rounded-sm mt-1">
                            <div
                              className={`h-1 rounded-sm ${consumo >= maxKg ? "bg-red-500" : "bg-[#B84A52]"}`}
                              style={{ width: `${Math.min(100, (consumo / maxKg) * 100)}%` }}
                            />
                          </div>
                        </>
                      )}
                      {c.cantidad_max_unidades_promo != null && (() => {
                        const maxU = c.cantidad_max_unidades_promo;
                        const consU = p.unidades_consumidas || 0;
                        return (
                          <>
                            <div className="mt-1">{consU.toLocaleString("es-AR", { maximumFractionDigits: 0 })} / {Number(maxU).toLocaleString("es-AR")} u</div>
                            <div className="h-1 bg-neutral-200 rounded-sm mt-1">
                              <div
                                className={`h-1 rounded-sm ${consU >= maxU ? "bg-red-500" : "bg-[#B84A52]"}`}
                                style={{ width: `${Math.min(100, (consU / maxU) * 100)}%` }}
                              />
                            </div>
                          </>
                        );
                      })()}
                    </td>
                    <td className="p-2 text-right">
                      <Button size="sm" variant="ghost" onClick={() => abrirEdit(p)} data-testid={`promo-edit-${p.id}`}>
                        <PencilSimple size={16} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => eliminar(p)} data-testid={`promo-delete-${p.id}`}>
                        <Trash size={16} className="text-red-600" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Diálogo Crear / Editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="promo-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar promoción" : "Nueva promoción"}</DialogTitle>
            <DialogDescription>
              Todas las condiciones son <b>opcionales</b>. Si dejás vacío un campo, esa
              condición no se aplica. La promo se activa cuando <b>todas las</b> definidas se cumplen.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Nombre *</Label>
              <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Ej: Martes de bondiola -15%" data-testid="promo-nombre" />
            </div>
            <div className="col-span-2">
              <Label>Descripción</Label>
              <Textarea rows={2} value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} placeholder="Detalles internos de la campaña" />
            </div>
            <div className="col-span-2">
              <Label>Código de cupón (opcional)</Label>
              <Input value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value.toUpperCase() })} placeholder="MARZO10" className="font-mono" />
            </div>

            {/* Descuento */}
            <div className="col-span-2 border-t pt-3 mt-2">
              <div className="text-xs font-bold uppercase text-neutral-500 mb-2">Descuento</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Tipo *</Label>
                  <Select value={form.descuento.tipo} onValueChange={(v) => setForm({ ...form, descuento: { ...form.descuento, tipo: v } })}>
                    <SelectTrigger data-testid="promo-desc-tipo"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TIPO_DESCUENTO.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Valor *</Label>
                  <NumberInput step="0.01" value={form.descuento.valor} onChange={(e) => setForm({ ...form, descuento: { ...form.descuento, valor: e.target.value } })} data-testid="promo-desc-valor" />
                </div>
              </div>
            </div>

            {/* Condiciones */}
            <div className="col-span-2 border-t pt-3 mt-2">
              <div className="text-xs font-bold uppercase text-neutral-500 mb-2">Condiciones (todas opcionales)</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Vigente desde</Label>
                  <DateInput value={form.condiciones.fecha_desde} onChange={(e) => setC("fecha_desde", e.target.value)} data-testid="promo-fecha-desde" />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label>Vigente hasta</Label>
                    <label className="flex items-center gap-1 text-xs text-neutral-600 cursor-pointer">
                      <Checkbox
                        checked={form.condiciones.fecha_hasta_indefinida}
                        onCheckedChange={(v) => setC("fecha_hasta_indefinida", !!v)}
                        data-testid="promo-fecha-hasta-indefinida"
                      />
                      <InfinityIcon size={12} /> Indefinida
                    </label>
                  </div>
                  <DateInput
                    value={form.condiciones.fecha_hasta}
                    onChange={(e) => setC("fecha_hasta", e.target.value)}
                    disabled={form.condiciones.fecha_hasta_indefinida}
                    data-testid="promo-fecha-hasta"
                  />
                </div>

                <div className="col-span-2">
                  <div className="flex items-center justify-between">
                    <Label>Días de la semana</Label>
                    <button
                      type="button"
                      onClick={setSemanaCompleta}
                      className={`text-xs px-2 py-0.5 rounded-sm border ${form.condiciones.dias_semana?.length === 7 ? "bg-[#B84A52] text-white border-[#B84A52]" : "bg-white border-neutral-300 text-neutral-600 hover:bg-neutral-50"}`}
                      data-testid="promo-toda-semana"
                    >
                      Toda la semana
                    </button>
                  </div>
                  <div className="flex gap-1 mt-1">
                    {DIAS.map((d) => (
                      <button
                        key={d.v}
                        type="button"
                        onClick={() => toggleDia(d.v)}
                        className={`px-2 py-1 text-xs rounded-sm border ${
                          form.condiciones.dias_semana?.includes(d.v)
                            ? "bg-[#B84A52] text-white border-[#B84A52]"
                            : "bg-white text-neutral-500 border-neutral-300"
                        }`}
                      >{d.l}</button>
                    ))}
                  </div>
                  <div className="text-xs text-neutral-400 mt-1">
                    {(!form.condiciones.dias_semana || form.condiciones.dias_semana.length === 0)
                      ? "Sin días seleccionados = aplica todos los días de la semana"
                      : ""}
                  </div>
                </div>

                <SearchableMultiSelect
                  label="Clientes específicos"
                  placeholder="Buscar cliente por nombre o CUIT…"
                  options={clientes.map((c) => ({ value: c.id, label: c.nombre, hint: c.cuit }))}
                  selected={form.condiciones.cliente_ids || []}
                  onChange={(v) => setC("cliente_ids", v)}
                  testid="promo-clientes"
                />
                <SearchableMultiSelect
                  label="Listas de precios aplicables"
                  placeholder="Buscar lista…"
                  options={listasPrecios.map((l) => ({ value: l.id, label: l.nombre }))}
                  selected={form.condiciones.lista_precios_ids || []}
                  onChange={(v) => setC("lista_precios_ids", v)}
                  testid="promo-listas"
                />

                <div className="col-span-2">
                  <SearchableMultiSelect
                    label="Productos específicos"
                    placeholder="Buscar producto por código o nombre…"
                    options={productos.map((p) => ({ value: p.id, label: p.nombre, hint: p.codigo }))}
                    selected={form.condiciones.producto_ids || []}
                    onChange={(v) => setC("producto_ids", v)}
                    testid="promo-productos"
                  />
                </div>

                <SearchableMultiSelect
                  label="Medios de pago habilitados"
                  placeholder="Buscar medio de pago…"
                  options={[
                    { value: "cta_cte", label: "Cuenta corriente", hint: "CTA CTE" },
                    ...cajas.map((c) => ({ value: c.id, label: c.nombre, hint: (c.tipo || "").toUpperCase() })),
                  ]}
                  selected={form.condiciones.medios_pago || []}
                  onChange={(v) => setC("medios_pago", v)}
                  testid="promo-medios-pago"
                />

                <div className="col-span-2">
                  <Label>Cantidad mínima por línea (kg)</Label>
                  <NumberInput step="0.01" value={form.condiciones.cantidad_min_kg} onChange={(e) => setC("cantidad_min_kg", e.target.value)} placeholder="ej: 2" data-testid="promo-cantidad-min-kg" />
                </div>

                <div className="col-span-2">
                  <Label>Cantidad máxima por línea (kg)</Label>
                  <NumberInput step="0.01" value={form.condiciones.cantidad_max_kg} onChange={(e) => setC("cantidad_max_kg", e.target.value)} placeholder="ej: 5 (vacío = sin tope)" data-testid="promo-cantidad-max-kg" />
                  <div className="text-[10px] text-neutral-500 mt-1">
                    Útil para escalonar: ej. "2 a 5 kg → 5% off; 5 kg o más → 10% off".
                  </div>
                </div>

                <div className="col-span-2">
                  <Label>Monto mínimo de factura ($) <span className="text-neutral-400 font-normal">— opcional</span></Label>
                  <NumberInput step="0.01" value={form.condiciones.monto_min_factura} onChange={(e) => setC("monto_min_factura", e.target.value)} placeholder="ej: 500000 (dejar vacío = sin mínimo)" data-testid="promo-monto-min-factura" />
                </div>

                <div className="col-span-2 bg-amber-50/60 border border-amber-200 rounded-sm p-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-[13px]">Tope total de kilos a vender con esta promoción</Label>
                    <label className="flex items-center gap-1 text-xs text-neutral-700 cursor-pointer">
                      <Checkbox
                        checked={form.condiciones.cantidad_max_kg_indefinida}
                        onCheckedChange={(v) => setC("cantidad_max_kg_indefinida", !!v)}
                        data-testid="promo-max-kg-indefinida"
                      />
                      <InfinityIcon size={12} /> Sin límite (indefinido)
                    </label>
                  </div>
                  <NumberInput
                    step="0.01"
                    value={form.condiciones.cantidad_max_kg_promo}
                    onChange={(e) => setC("cantidad_max_kg_promo", e.target.value)}
                    disabled={form.condiciones.cantidad_max_kg_indefinida}
                    placeholder="ej: 500 (kg totales acumulados)"
                    className="mt-1"
                    data-testid="promo-max-kg-value"
                  />
                  <div className="text-[11px] text-neutral-500 mt-1">
                    Cuando el total de kilos vendidos con esta promoción alcance el tope, la promo dejará de aplicarse automáticamente.
                  </div>
                </div>

                <div className="col-span-2 bg-amber-50/60 border border-amber-200 rounded-sm p-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-[13px]">Tope total de unidades a vender con esta promoción</Label>
                    <label className="flex items-center gap-1 text-xs text-neutral-700 cursor-pointer">
                      <Checkbox
                        checked={form.condiciones.cantidad_max_unidades_indefinida}
                        onCheckedChange={(v) => setC("cantidad_max_unidades_indefinida", !!v)}
                        data-testid="promo-max-unidades-indefinida"
                      />
                      <InfinityIcon size={12} /> Sin límite (indefinido)
                    </label>
                  </div>
                  <NumberInput
                    step="1"
                    value={form.condiciones.cantidad_max_unidades_promo}
                    onChange={(e) => setC("cantidad_max_unidades_promo", e.target.value)}
                    disabled={form.condiciones.cantidad_max_unidades_indefinida}
                    placeholder="ej: 100 (unidades totales acumuladas)"
                    className="mt-1"
                    data-testid="promo-max-unidades-value"
                  />
                  <div className="text-[11px] text-neutral-500 mt-1">
                    Cuando el total de unidades vendidas con esta promoción alcance el tope, la promo dejará de aplicarse automáticamente.
                  </div>
                </div>
              </div>
            </div>

            {/* Flags */}
            <div className="col-span-2 border-t pt-3 mt-2 flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch id="promo-activo" checked={form.activo} onCheckedChange={(v) => setForm({ ...form, activo: v })} data-testid="promo-activo" />
                <Label htmlFor="promo-activo" className="cursor-pointer">Activa</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch id="promo-acumulable" checked={form.acumulable} onCheckedChange={(v) => setForm({ ...form, acumulable: v })} data-testid="promo-acumulable" />
                <Label htmlFor="promo-acumulable" className="cursor-pointer">Acumulable con otras promos</Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={submit} data-testid="promo-guardar-btn">
              {editing ? "Guardar cambios" : "Crear promoción"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** MultiSelect con buscador (input filtrable) y checkboxes.
 * options: [{ value, label, hint? }]
 */
function SearchableMultiSelect({ label, placeholder, options, selected, onChange, testid }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return options;
    return options.filter((o) =>
      (o.label || "").toLowerCase().includes(s) || (o.hint || "").toLowerCase().includes(s)
    );
  }, [options, q]);
  const toggle = (v) => {
    if (selected.includes(v)) onChange(selected.filter((x) => x !== v));
    else onChange([...selected, v]);
  };
  const clearAll = () => onChange([]);
  const selectAllFiltered = () => {
    // Selecciona los visibles según el filtro actual (o TODOS si no hay filtro),
    // preservando cualquier valor previamente seleccionado que quedó fuera del filtro.
    const visibleValues = filtered.map((o) => o.value);
    const merged = Array.from(new Set([...selected, ...visibleValues]));
    onChange(merged);
  };
  const invertFiltered = () => {
    // Invertir dentro de lo filtrado: destildar los que ya están, tildar los que no.
    // Útil para el flujo "seleccionar todo y excluir uno".
    const visibleValues = filtered.map((o) => o.value);
    const visSet = new Set(visibleValues);
    const kept = selected.filter((v) => !visSet.has(v));
    const added = visibleValues.filter((v) => !selected.includes(v));
    onChange([...kept, ...added]);
  };
  const totalCount = options.length;
  const allSelected = totalCount > 0 && selected.length === totalCount;

  return (
    <div>
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-neutral-500">
            {selected.length ? `${selected.length}/${totalCount} seleccionado(s)` : ""}
          </span>
          <button
            type="button"
            onClick={allSelected ? clearAll : selectAllFiltered}
            className="text-[11px] text-[#6B1F24] hover:underline font-mono"
            data-testid={testid ? `${testid}-select-all` : undefined}
            title={allSelected ? "Deseleccionar todo" : q ? "Seleccionar todo lo filtrado (para excluir sólo lo que quede fuera)" : "Seleccionar todo (para excluir uno tildándolo/destildándolo)"}
          >
            {allSelected ? "Deseleccionar todo" : q ? "Seleccionar filtrados" : "Seleccionar todo"}
          </button>
          {q && filtered.length > 0 && (
            <button
              type="button"
              onClick={invertFiltered}
              className="text-[11px] text-neutral-600 hover:underline font-mono"
              data-testid={testid ? `${testid}-invert` : undefined}
              title="Invertir la selección dentro de lo filtrado"
            >
              Invertir
            </button>
          )}
        </div>
      </div>
      <div className="border border-neutral-200 rounded-sm bg-white">
        <div className="relative border-b">
          <MagnifyingGlass size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={placeholder || "Buscar…"}
            className="w-full pl-7 pr-2 py-1.5 text-xs outline-none"
            data-testid={testid ? `${testid}-search` : undefined}
          />
        </div>
        <div className="max-h-36 overflow-y-auto text-xs">
          {filtered.length === 0 ? (
            <div className="text-neutral-400 p-2">Sin coincidencias</div>
          ) : filtered.map((o) => (
            <label key={o.value} className="flex items-center gap-1.5 px-2 py-1 hover:bg-neutral-50 cursor-pointer">
              <input
                type="checkbox"
                checked={selected.includes(o.value)}
                onChange={() => toggle(o.value)}
                data-testid={testid ? `${testid}-opt-${o.value}` : undefined}
              />
              <span className="truncate">{o.label}</span>
              {o.hint && <span className="ml-auto text-neutral-400 font-mono text-[10px]">{o.hint}</span>}
            </label>
          ))}
        </div>
        {selected.length > 0 && (
          <div className="border-t px-2 py-1 flex items-center justify-between bg-neutral-50">
            <span className="text-[10px] text-neutral-500">Selección: {selected.length}</span>
            <button type="button" onClick={clearAll} className="text-[10px] text-red-600 hover:underline">
              Limpiar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
